import { useContext, useEffect, useState } from "react";
import ImagePreview from "../../../../components/image.preview";
import { Upload, message, Form, Flex } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import {
  createOrUpdateCase,
  createOrUpdateCaseImage,
} from "../../../../services";
import { ActionType } from "./case.details";
import { useNavigate } from "react-router-dom";
import { PageContext } from "../../../../components/layout/page";
import Scenario, { getThemesFromCases } from "./scenario";
import { cpdContentCasesState } from "../../../../state";
import { useAtom } from "jotai";

const CreateOrUpdateCase = ({
  currentElement,
  isPreviewOn,
  setIsPreviewOn = (bool) => null,
  formType,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(
    currentElement?._links?.image?.href
  );
  const { setHeaderText } = useContext(PageContext);

  const [cases, setCases] = useAtom(cpdContentCasesState, {
    delay: 0,
  });

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (currentElement?.uid) {
      setHeaderText(currentElement?.theme);
      form?.setFieldsValue({
        title: currentElement?.title || "",
        theme: currentElement?.theme?.name || "",
        scenario: currentElement?.scenario || "",
        status: currentElement?.status || "",
        keyLearningPoints: currentElement?.keyLearningPoints
          ? currentElement?.keyLearningPoints.flatMap((point) => point.description)[0]
          : "",
      });
    } else {
      form.resetFields();
    }
  }, [currentElement]);

  const updateOrCreateSuccess = (action) => {
    messageApi.open({
      key: "CREATE_OR_UPDATE",
      type: "loading",
      content: `${action} in progress...`,
    });
  };

  // Remove image
  const handleRemoveImage = ({ fileList }) => {
    message.success("Image removed");
  };

  // Preview image
  const handlePreview = (file) => {
    setPreviewImage(file.url || file.thumbUrl);
    setIsPreviewOn(false);
  };

  // Fix for defaultMutationOptions error: use correct mutation signature
  const mutation = useMutation({
    mutationFn: createOrUpdateCase,
    onSuccess: (response) => {
      setCases(
        cases?.map((_case) => {
          if (_case?.uid === response?.data?.uid) {
            return { ...response?.data };
          }
          return _case;
        })
      );
      messageApi.success("Request successful");
      messageApi.destroy("CREATE_OR_UPDATE");
      navigate("/manage/cpd/cases");
    },
    onError: () => message.error("Request failed"),
  });

  const imageMutation = useMutation({
    mutationFn: createOrUpdateCaseImage,
    onSuccess: (response) => {
      setCases(
        cases?.map((_case) => {
          if (_case?.uid === response?.data?.uid) {
            return { ...response?.data };
          }
          return _case;
        })
      );
      messageApi.success("Image updated successfully");
      messageApi.destroy("CREATE_OR_UPDATE");
    },
    onError: (error) => {
      console.log(
        error?.response?.data?.errors?.map((error) => error).join(";")
      );
      message.error(
        `Failed to update image for case ${
          currentElement?.uid
        }. ${error?.response?.data?.errors?.map((error) => error).join(";")}`
      );
      messageApi.destroy("CREATE_OR_UPDATE");
    },
  });

  // Handle image upload
  const handleImageChange = ({ fileList }) => {
    imageMutation.mutate({
      image: fileList[0],
      caseId: currentElement?.uid,
    });
    updateOrCreateSuccess("Update");
  };

  // Save changes
  const handleSave = () => {
    form.validateFields({
      dirty: true,
    });
    updateOrCreateSuccess("Update");

    const { theme, keyLearningPoints, ...rest } = form?.getFieldsValue();
    const payload = {
      ...rest,
      uid: currentElement?.uid,
      themeUid: currentElement?.theme?.uid || theme,
      keyLearningPoints: [{uid: currentElement?.keyLearningPoints[0]?.uid, description: keyLearningPoints}],
    };
    mutation.mutate({ body: payload });
    setIsPreviewOn(false);
  };

  // Cancel editing
  const handleCancel = () => {
    setImages(currentElement?.images || []);
    setIsPreviewOn(false);
    navigate("/manage/cpd/cases");
  };

  return (
    <Form
      form={form}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      {contextHolder}
      <Flex flex="row">
        {formType !== ActionType.CREATE_NEW_CASE && (
          <ImagePreview images={currentElement?._links?.image?.href} />
        )}
        {isPreviewOn && (
          <Upload
            listType="picture-card"
            fileList={images}
            onRemove={handleRemoveImage}
            onChange={handleImageChange}
            onPreview={handlePreview}
            beforeUpload={() => false}
            showUploadList={{ showRemoveIcon: true }}
            customRequest={handleImageChange}
          >
            <div>
              <UploadOutlined width={200} height={200} />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          </Upload>
        )}
      </Flex>

      {currentElement ? (
        <Scenario
          currentElement={currentElement}
          handleCancel={handleCancel}
          handleSave={handleSave}
          isPreviewOn={isPreviewOn}
          formType={formType}
          form={form}
          themes={getThemesFromCases({ cases })}
          keyLearningPoints={currentElement?.keyLearningPoints || []}
        />
      ) : (
        <Scenario
          currentElement={null}
          formType={formType}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          isPreviewOn={true}
          themes={getThemesFromCases({ cases })}
        />
      )}
    </Form>
  );
};

export default CreateOrUpdateCase;
